import { useState, useEffect, useCallback } from "react";

import { loadStripe } from "@stripe/stripe-js";

import configEntzy from "components/config/ConfigEntzy";
import {
  serviceActionCapture,
  serviceActionListGet,
  serviceSyncDevice,
  serviceJoinInvite,
  serviceCookieSet,
  servicePaymentMethodSet,
  servicePaymentConfirm,
} from "services/graphql/call";

// listener for top level app actions
// actions that can be triggered:
// - from querystring parameters (e.g. from external links and oauth flows)
// - from other parts of the app via cookie (e.g. after login to trigger post login actions)
export const useMainActions = (options = {}) => {
  const { identifying, user, userReload, navigate, location, searchParams } =
    options;

  const [actionLoading, setActionLoading] = useState(0);
  const [appAlert, setAppAlert] = useState({ show: false, message: "" });
  const [autoJoinEvent, setAutoJoinEvent] = useState(null);

  const appAlertClear = useCallback(() => {
    setAppAlert({ show: false, message: "" });
  }, []);

  const autoJoinEventClear = () => {
    setAutoJoinEvent({ EventId: null });
  };

  // action execution
  // list of action types:
  // - push: register device for push notifications
  // - join: join event from invite
  // - redirect: redirect to path
  // - stripe_setup_intent_callback: handle stripe setup intent callback
  // - stripe_payment_intent_callback: handle stripe payment intent callback
  const queryStringActionListExecute = useCallback(async () => {
    const actionList = serviceActionListGet();

    // action loader handling
    // needs to be separate from processing as causes re-render
    if (actionLoading === 0 && actionList.length > 0) {
      let loader = 1;
      for (let action of actionList) {
        if (
          [
            configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key,
            configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key,
          ].includes(action.type)
        ) {
          loader = 2;
          break;
        }
      }
      setActionLoading(loader);
    }

    // action start
    if (actionLoading > 0 && actionList.length > 0) {
      // action pre-processing
      // first clear the action cookie list to avoid re-execution
      // serviceActionListClear(); // does not work on ios
      serviceCookieSet("actionList", "[]", 0);

      // action processing
      let alertSeverity;
      let alertMessage = "";
      let autoJoinEvent;
      for (let action of actionList) {
        switch (action.type) {
          case "push":
            action.status = await serviceSyncDevice(user, action);
            break;
          case "join":
            action.status = await serviceJoinInvite(user, action);
            break;
          case "redirect":
            action.status = { success: true, alert: false };
            navigate(action.path);
            break;
          case configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key:
            action.stripe = await loadStripe(
              configEntzy.APP_PAYMENTS_PUBLIC_KEY,
            );
            action.status = await servicePaymentMethodSet(user, action);
            // await cbSectionUserReload();
            await userReload();
            break;
          case configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key:
            action.stripe = await loadStripe(
              configEntzy.APP_PAYMENTS_PUBLIC_KEY,
            );
            action.status = await servicePaymentConfirm(user, action);
            break;
          default:
            action.status = {
              success: false,
              alert: false,
              message: "Invalid action type",
            };
            break;
        }

        // track any alerts to show after processing all actions
        if (action.status.alert) {
          alertMessage += action.status.message + " ";
          if (action.status.success && alertSeverity !== "error") {
            alertSeverity = "info";
          } else {
            alertSeverity = "error";
          }
        }
        if (action.status.autoJoinEvent) {
          autoJoinEvent = action.status.autoJoinEvent;
        }
      }

      // action post-processing
      setActionLoading(-1);
      if (alertSeverity) {
        setAppAlert({
          show: true,
          severity: alertSeverity,
          message: alertMessage,
        });
      }
      if (autoJoinEvent) {
        setAutoJoinEvent(autoJoinEvent);
      }
    }
    return actionList;
  }, [actionLoading, navigate, user, userReload]);

  // action capture
  // captures action payload by type and prepares to execute
  // immediately if user connected or stores in cookies for processing after connection
  const queryStringActionCapture = useCallback(
    async (action) => {
      console.log("Capturing action", action);
      switch (action.type) {
        case "push":
          action.platform = decodeURIComponent(searchParams.get("platform"));
          action.token = decodeURIComponent(searchParams.get("token"));
          action.status = await serviceActionCapture(action);
          break;
        case "join":
          action.eid = decodeURIComponent(searchParams.get("eid"));
          action.token = decodeURIComponent(searchParams.get("token"));
          action.status = await serviceActionCapture(action);
          // flag an info alert if user collecting invite before connecting
          if (!user.connected && action.status.success) {
            action.status.info = true;
            action.status.title = "Invite Ready";
            action.status.message =
              "Your invite is ready to be accepted. To continue please connect with your preferred provider using the navigation at the top.";
          }
          break;
        case configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key:
          action.setupIntentId = decodeURIComponent(
            searchParams.get(
              configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key,
            ),
          );
          action.clientSecret = decodeURIComponent(
            searchParams.get(
              configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_secret_key,
            ),
          );
          action.status = await serviceActionCapture(action);
          break;
        case configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key:
          action.paymentIntentId = decodeURIComponent(
            searchParams.get(
              configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key,
            ),
          );
          action.clientSecret = decodeURIComponent(
            searchParams.get(
              configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_secret_key,
            ),
          );
          action.status = await serviceActionCapture(action);
          break;
        default:
          action.status = {
            success: false,
            alert: true,
            message: "Invalid action type",
          };
          break;
      }

      // check results
      let result = [action];
      if (!action.status.success && action.status.alert) {
        setAppAlert({
          show: true,
          severity: "error",
          message: action.status.message,
        });
        return result;
      }
      if (action.status.info) {
        setAppAlert({
          show: true,
          severity: "info",
          title: action.status.title,
          message: action.status.message,
        });
      }

      // execute immediately if user connected
      // let executeNow =
      //   user.connected ||
      //   [configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key].includes(
      //     action.type,
      //   );
      if (user.connected) {
        result = await queryStringActionListExecute();
      }
      return result;
    },
    [user, searchParams, queryStringActionListExecute],
  );

  // reset querystring and navigate to current path
  // to clear any action related parameters
  const queryStringClear = useCallback(() => {
    for (let item of searchParams) {
      searchParams.delete(item.key);
    }
    navigate(location.pathname);
  }, [searchParams, navigate, location]);

  // action listener: check path and querystring
  // listeners required for both guest and connected users
  useEffect(() => {
    if (!identifying && user) {
      // first any users: connected or not
      // capture any actions from querystring
      if (searchParams.has("action")) {
        queryStringClear();
        setTimeout(() => {
          queryStringActionCapture({
            type: searchParams.get("action"),
          });
        }, 500);
      }

      // check for user profile requests and redirect /@name to /@/name
      if (
        location.pathname.startsWith("/@") &&
        !location.pathname.startsWith("/@/")
      ) {
        const path = location.pathname.replace("/@", "/@/");
        navigate(path);
      }

      // connected users: protected actions
      if (user.connected) {
        // calendar integrations: check for oauth code with calendar scope
        if (searchParams.has("code") && searchParams.has("scope")) {
          const code = searchParams.get("code");
          const scope = searchParams.get("scope");
          if (scope.includes("https://www.googleapis.com/auth/calendar")) {
            serviceCookieSet(
              configEntzy.CALENDAR_OAUTH_COOKIE,
              JSON.stringify({ code, provider: "google" }),
              0,
            );
            navigate("/calendar");
            setTimeout(() => {
              setAppAlert({
                show: true,
                severity: "info",
                title: "Google Calendar Connected",
                message:
                  "Your calendar has been connected successfully. You can now view and manage entries from your Entzy calendar.",
              });
            }, 2000);
          }
        }

        // stripe setup intent callbacks
        if (
          searchParams.has(configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key)
        ) {
          queryStringClear();
          setTimeout(() => {
            queryStringActionCapture({
              type: configEntzy.STRIPE_SETUP_INTENT_PARAMS.callback_key,
            });
          }, 500);
        }
        // stripe payment intent callbacks
        if (
          searchParams.has(
            configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key,
          )
        ) {
          queryStringClear();
          setTimeout(() => {
            queryStringActionCapture({
              type: configEntzy.STRIPE_PAYMENT_INTENT_PARAMS.callback_key,
            });
          }, 500);
        }
        // invoke any actions in list when user connected
        queryStringActionListExecute();
      }
    }
  }, [
    identifying,
    user,
    userReload,
    actionLoading,
    searchParams,
    location,
    navigate,
    queryStringActionCapture,
    queryStringActionListExecute,
    queryStringClear,
  ]);

  return [
    actionLoading,
    appAlert,
    appAlertClear,
    autoJoinEvent,
    autoJoinEventClear,
  ];
};

// USAGE
// const [actionLoading, appAlert, appAlertClear, autoJoinEvent, autoJoinEventClear] = useMainActions({ identifying, user, userReload, navigate, location, searchParams });
